import {
    createContext,
    useContext,
    useMemo,
    ReactNode,
    ReactElement
} from 'react'
import { useUser } from '@corratech/pylot-auth-manager'
import {
    useWishlistActions,
    useWishlist,
    useRemoveProductsFromWishlist
} from '@corratech/pylot-wishlist'
import { AddWishlistVariableInterface } from '@corratech/pylot-wishlist/src/Hooks/use-add-products-to-wishlist'
import { GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import {
    MutationRemoveProductsFromWishlistArgs,
    RemoveProductsFromWishlistOutput,
    Maybe,
    Wishlist
} from '@pylot-data/pylotschema'

export interface IRemoveProductsFromWishlistOutput {
    removeProductsFromWishlist: RemoveProductsFromWishlistOutput
}

type WishlistContextType = {
    isSignedIn: boolean
    handleAddWishlistAction: (
        itemsData: AddWishlistVariableInterface,
        isConfig: boolean,
        stayOnPage?: boolean,
        onSuccess?: () => void
    ) => void
    isWishlistActionsLoading?: boolean
    isWishlistLoading: boolean
    wishlists: Array<Maybe<Wishlist>> | undefined
    isRemoveProductFromWishlistLoading?: boolean
    removeProductsFromWishlist: (
        formInput: MutationRemoveProductsFromWishlistArgs
    ) => Promise<GraphqlFetchOutput<IRemoveProductsFromWishlistOutput>>
    isUserLoading: boolean
    isAddLoading: boolean
}

export const WishlistContext = createContext<WishlistContextType>({
    isSignedIn: false,
    handleAddWishlistAction: () => null,
    isWishlistActionsLoading: false,
    isWishlistLoading: false,
    wishlists: [],
    isRemoveProductFromWishlistLoading: false,
    removeProductsFromWishlist: () =>
        new Promise<GraphqlFetchOutput<IRemoveProductsFromWishlistOutput>>(
            (resolve) =>
                resolve(
                    {} as GraphqlFetchOutput<IRemoveProductsFromWishlistOutput>
                )
        ),
    isUserLoading: false,
    isAddLoading: false
})

export const WishlistProvider = ({
    children
}: {
    children: ReactNode
}): ReactElement => {
    const { isSignedIn, loading: isUserLoading } = useUser()
    const { handleAddWishlistAction, isLoading: isAddLoading } =
        useWishlistActions()
    const { loading: isWishlistLoading, wishlists } = useWishlist()
    const { removeProductsFromWishlist } = useRemoveProductsFromWishlist()

    const value = useMemo(
        () => ({
            isSignedIn,
            handleAddWishlistAction,
            isWishlistLoading,
            wishlists,
            removeProductsFromWishlist: removeProductsFromWishlist as (
                formInput: MutationRemoveProductsFromWishlistArgs
            ) => Promise<GraphqlFetchOutput<IRemoveProductsFromWishlistOutput>>,
            isUserLoading,
            isAddLoading
        }),
        [
            isSignedIn,
            handleAddWishlistAction,
            isWishlistLoading,
            wishlists,
            removeProductsFromWishlist,
            isUserLoading,
            isAddLoading
        ]
    )

    return (
        <WishlistContext.Provider value={value}>
            {children}
        </WishlistContext.Provider>
    )
}

export const useWishlistContext = (): WishlistContextType => {
    const context = useContext(WishlistContext)

    if (!context) {
        throw new Error(
            'useWishlistContext must be used within a WishlistProvider'
        )
    }

    return context
}
